import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button, Divider, List } from '@bosch/react-frok'

import { useModal } from '@/contexts/modalContext'
import type {
    VersionHistoryProps,
    VersionModalContentProps,
} from './types/VersionHistoryTypes'

import './VersionHistory.scss'

const topicList: Array<{ titleKey: string; tag: 'changes' | 'fixes' }> = [
    { titleKey: 'general:header:changes', tag: 'changes' },
    { titleKey: 'general:header:resolvedIssues', tag: 'fixes' },
]

export const VersionModalContent = ({ versionHistory }: VersionModalContentProps) => {
    const { t, i18n } = useTranslation()
    const language = i18n.resolvedLanguage || i18n.language || 'en'
    const releaseNotesEnglishOnly = language !== 'en'

    const getSectionLabel = (key: string): string => {
        if (!releaseNotesEnglishOnly) {
            return t(key)
        }

        const englishLabels: Record<string, string> = {
            'general:header:version': 'Version',
            'general:header:changes': 'Changes',
            'general:header:resolvedIssues': 'Resolved issues',
        }

        return englishLabels[key] ?? t(key)
    }

    return (
        <div className="version-notes-wrapper">
            {releaseNotesEnglishOnly && (
                <div className="release-notes-language-note">
                    Release notes are currently available in English only.
                </div>
            )}
            {versionHistory &&
                versionHistory.map((version, index) => {
                    let versionTitle = `${getSectionLabel('general:header:version')}: MMA v${version?.version}`

                    if (version?.name)
                        versionTitle += ` (${version?.name})`

                    return (
                        <div className="version-container" key={index}>
                            <div className="version-header">
                                <div className="version-title title">{versionTitle}</div>
                                <div className="version-date">{version.date}</div>
                            </div>
                            <Divider />
                            <div className="version-content">
                                {topicList?.map((topic, index) => {
                                    return (
                                        version[topic.tag] &&
                                        <div key={index} className="topic-container">
                                            <div className="topic-title -size-m">{getSectionLabel(topic.titleKey)}</div>
                                            <div className={"content content-list-only"}>
                                                <List items={version[topic.tag]} />
                                            </div>
                                        </div>
                                    )
                                })}

                            </div>
                            <Divider />
                            <div className="last-divider">
                            </div>
                        </div>
                    )
                })}
        </div>
    );
}

function VersionHistory({ history, shortVersion, toggleMenu }: VersionHistoryProps) {
    const { t } = useTranslation()
    const [label, setLabel] = useState<string>(`${t('general:header:version')} 0.0`)
    const { showModal, hideModal } = useModal();

    useEffect(() => {
        const currentVersion = history?.[0] || undefined

        if (currentVersion) {
            if (shortVersion)
                setLabel(`${currentVersion.version}`)
            else
                setLabel(`${t('general:header:version')} ${currentVersion.version} | ${currentVersion.name}`)
        }
    }, [shortVersion, history, t])

    const showHistoryAndToggleMenu = (): void => {
        showModal({
            title: " ",
            content: <VersionModalContent versionHistory={history} />,
            confirmLabel: t('general:buttons:ok'),
            onConfirmClick: async () => {
                hideModal()
            },
        });
        toggleMenu(false)
    }

    return (
        <div className='version-history'>
            <Button className='navigation-text' title={t('general:header:versionHistory')} mode='integrated' onClick={showHistoryAndToggleMenu}>
                {label}
            </Button>
        </div>
    )
}

export default VersionHistory